import { api } from '../client';
import type { ChainId } from '@/stores/slices/chainSlice';
import type {
  BundleAnalysisOutcome,
  BundleAnalysisResponse,
  BundleAnalysisTradersResponse,
  CandlestickActivityResponse,
  SellPressureResponse,
  SimilarRecentTokensResponse,
  TokenResponse,
  SwapsResponse,
  TradesByTradersResponse,
  TokenTradersResponse,
  RecentTraderIntelligenceResponse,
  DumpRiskPointParams,
  DumpRiskPointResponse,
  DumpRiskSeriesParams,
  DumpRiskSeriesResponse,
  PumpProbabilityPointParams,
  PumpProbabilityPointResponse,
  PumpProbabilitySeriesParams,
  PumpProbabilitySeriesResponse,
  Pump50Next60mSeriesParams,
  Pump50Next60mSeriesResponse,
  Pump100Next60mSeriesParams,
  Pump100Next60mSeriesResponse,
  Pump50HoldNext60mSeriesParams,
  Pump50HoldNext60mSeriesResponse,
  Pump50EntryNext60mSeriesParams,
  Pump50EntryNext60mSeriesResponse,
} from '@/types/api';

/**
 * Chain-aware Token API endpoints
 */
export const chainTokenAPI = {
  /**
   * Get token details with main pair info
   */
  getToken: async (chain: ChainId, tokenAddress: string): Promise<TokenResponse> => {
    return api.get<TokenResponse>(`/api/${chain}/tokens/${tokenAddress}`);
  },

  /**
   * Get latest swaps for a token's pair
   */
  getLatestSwaps: async (
    chain: ChainId,
    tokenAddress: string,
    pairAddress: string,
    params?: { page?: number; per_page?: number }
  ): Promise<SwapsResponse> => {
    return api.get<SwapsResponse>(`/api/${chain}/tokens/${tokenAddress}/swaps/latest`, {
      params: { ...params, pair_address: pairAddress },
    });
  },

  /**
   * Get earliest swaps for a token's pair
   */
  getEarliestSwaps: async (
    chain: ChainId,
    tokenAddress: string,
    pairAddress: string,
    params?: { page?: number; per_page?: number }
  ): Promise<SwapsResponse> => {
    return api.get<SwapsResponse>(`/api/${chain}/tokens/${tokenAddress}/swaps/earliest`, {
      params: { ...params, pair_address: pairAddress },
    });
  },

  /**
   * Get traders for a token (wallet-level, default sorted by first buy time)
   */
  getTraders: async (
    chain: ChainId,
    tokenAddress: string,
    params?: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
      earliest_buy_from?: string;
      earliest_buy_to?: string;
      buy_count_min?: string;
      buy_count_max?: string;
      sell_count_min?: string;
      sell_count_max?: string;
      bought_usd_min?: string;
      bought_usd_max?: string;
      sold_usd_min?: string;
      sold_usd_max?: string;
      realized_pnl_min?: string;
      realized_pnl_max?: string;
      pnl_percent_min?: string;
      pnl_percent_max?: string;
      tainted_pnl?: string;
    }
  ): Promise<TokenTradersResponse> => {
    return api.get<TokenTradersResponse>(`/api/${chain}/tokens/${tokenAddress}/traders`, {
      params,
    });
  },

  /**
   * Get trades for a token by wallet address
   */
  getTradesByWallet: async (
    chain: ChainId,
    tokenAddress: string,
    walletAddress: string,
    params?: { page?: number; per_page?: number }
  ): Promise<any> => {
    return api.get<any>(`/api/${chain}/tokens/${tokenAddress}/trades/by_wallet`, {
      params: { ...params, wallet_address: walletAddress },
    });
  },

  /**
   * Get trades for a token by multiple trader addresses (up to 1000)
   */
  getTradesByTraders: async (
    chain: ChainId,
    tokenAddress: string,
    traderAddresses: string[]
  ): Promise<TradesByTradersResponse> => {
    return api.get<TradesByTradersResponse>(
      `/api/${chain}/tokens/${tokenAddress}/trades/by_traders`,
      {
        params: { trader_addresses: traderAddresses.join(',') },
      }
    );
  },

  /**
   * Get all trades for a token, paginated
   */
  getAllTrades: async (
    chain: ChainId,
    tokenAddress: string,
    params?: { page?: number; per_page?: number; sort_order?: 'asc' | 'desc' }
  ): Promise<any> => {
    return api.get<any>(`/api/${chain}/tokens/${tokenAddress}/trades/all`, { params });
  },

  /**
   * Get aggregated trader snapshot stats for a token in a time window
   */
  getTraderSnapshot: async (
    chain: ChainId,
    tokenAddress: string,
    from: string,
    to: string,
    options?: { include_distributions?: boolean; max_traders?: number }
  ): Promise<any> => {
    return api.get<any>(`/api/${chain}/tokens/${tokenAddress}/trader_snapshot`, {
      params: {
        from,
        to,
        ...(options?.include_distributions ? { include_distributions: '1' } : {}),
        ...(options?.max_traders ? { max_traders: options.max_traders } : {}),
      },
    });
  },

  /**
   * Get trader behavior before, during, and after one chart candle window.
   */
  getCandlestickActivity: async (
    chain: ChainId,
    tokenAddress: string,
    params: {
      from: number;
      to: number;
      page?: number;
      per_page?: number;
      ledger_page?: number;
      ledger_per_page?: number;
    }
  ): Promise<CandlestickActivityResponse> => {
    return api.get<CandlestickActivityResponse>(
      `/api/${chain}/tokens/${tokenAddress}/candlestick_activity`,
      {
        params,
      }
    );
  },

  /**
   * EXPERIMENTAL: projected near-term sell pressure for a token as of a candle.
   */
  getSellPressure: async (
    chain: ChainId,
    tokenAddress: string,
    params: {
      to: number;
      price?: number;
      lookback_seconds?: number;
      max_holders?: number;
      min_buy_usd?: number;
    }
  ): Promise<SellPressureResponse> => {
    return api.get<SellPressureResponse>(`/api/${chain}/tokens/${tokenAddress}/sell_pressure`, {
      params,
    });
  },

  /**
   * Get collapsed launch-window block summaries for a token.
   */
  getBundleAnalysis: async (
    chain: ChainId,
    tokenAddress: string,
    params?: {
      anchor?: 'first_trade' | 'dex_launch';
      start_block?: number;
      block_count?: number;
      include_empty_blocks?: boolean;
    }
  ): Promise<BundleAnalysisResponse> => {
    return api.get<BundleAnalysisResponse>(`/api/${chain}/tokens/${tokenAddress}/bundle_analysis`, {
      params: {
        ...params,
        ...(params?.include_empty_blocks !== undefined
          ? { include_empty_blocks: params.include_empty_blocks ? 'true' : 'false' }
          : {}),
      },
    });
  },

  /**
   * Get paginated trader rows for one expanded bundle-analysis block.
   */
  getBundleAnalysisBlockTraders: async (
    chain: ChainId,
    tokenAddress: string,
    blockNumber: number,
    params?: {
      page?: number;
      per_page?: number;
      sort_by?: 'initial_buy_usd' | 'status' | 'wallet_quality' | 'wallet_address';
      sort_order?: 'asc' | 'desc';
      status?: BundleAnalysisOutcome;
    }
  ): Promise<BundleAnalysisTradersResponse> => {
    return api.get<BundleAnalysisTradersResponse>(
      `/api/${chain}/tokens/${tokenAddress}/bundle_analysis/blocks/${blockNumber}/traders`,
      { params }
    );
  },

  /**
   * Get same-chain recent tokens ranked by fuzzy identity, volume, and recency.
   */
  getSimilarRecentTokens: async (
    chain: ChainId,
    tokenAddress: string,
    params?: { lookback_days?: number; limit?: number }
  ): Promise<SimilarRecentTokensResponse> => {
    return api.get<SimilarRecentTokensResponse>(
      `/api/${chain}/tokens/${tokenAddress}/similar_recent`,
      { params }
    );
  },

  /**
   * Calculate a bounded, observed-swap summary of a token's recent traders.
   * This endpoint is intentionally invoked only after an explicit user action.
   */
  getRecentTraderIntelligence: async (
    chain: ChainId,
    tokenAddress: string,
    params?: { lookback_seconds?: number; limit?: number }
  ): Promise<RecentTraderIntelligenceResponse> => {
    return api.get<RecentTraderIntelligenceResponse>(
      `/api/${chain}/tokens/${tokenAddress}/recent_trader_intelligence`,
      { params }
    );
  },
};

/**
 * V13's public browser boundary is intentionally Solana-only. Keeping these
 * calls out of the generic chain API prevents callers from constructing an
 * unsupported EVM route while still using the shared authenticated client.
 */
export const dumpRiskAPI = {
  getPoint: async (
    tokenAddress: string,
    params: DumpRiskPointParams
  ): Promise<DumpRiskPointResponse> => {
    return api.get<DumpRiskPointResponse>(`/api/sol/tokens/${tokenAddress}/dump_risk`, { params });
  },

  getSeries: async (
    tokenAddress: string,
    params: DumpRiskSeriesParams
  ): Promise<DumpRiskSeriesResponse> => {
    return api.get<DumpRiskSeriesResponse>(`/api/sol/tokens/${tokenAddress}/dump_risk_samples`, {
      params,
    });
  },
};

/**
 * V14's authenticated browser scoring boundary stays distinct from V13 because
 * pump and dump probabilities are independent. Strategy forms only persist
 * settings; buy-time scoring and enforcement remain server-side.
 */
export const pumpProbabilityAPI = {
  getPoint: async (
    tokenAddress: string,
    params: PumpProbabilityPointParams
  ): Promise<PumpProbabilityPointResponse> => {
    return api.get<PumpProbabilityPointResponse>(
      `/api/sol/tokens/${tokenAddress}/pump_probability`,
      { params }
    );
  },

  getSeries: async (
    tokenAddress: string,
    params: PumpProbabilitySeriesParams
  ): Promise<PumpProbabilitySeriesResponse> => {
    return api.get<PumpProbabilitySeriesResponse>(
      `/api/sol/tokens/${tokenAddress}/pump_probability_samples`,
      { params }
    );
  },
};

/**
 * V19 is a read-only, Solana-only research viewer. The browser displays the
 * server-authored probability of a 50%+ rise during the next 60 minutes on the
 * model's five-second first-hour grid. It never interpolates support gaps.
 */
const PUMP_NEXT60M_REQUEST_TIMEOUT_MS = 150_000;

export const pump50Next60mAPI = {
  getSeries: async (
    tokenAddress: string,
    params: Pump50Next60mSeriesParams,
    signal?: AbortSignal
  ): Promise<Pump50Next60mSeriesResponse> => {
    return api.get<Pump50Next60mSeriesResponse>(
      `/api/sol/tokens/${tokenAddress}/pump50_next60m_samples`,
      {
        params,
        timeout: PUMP_NEXT60M_REQUEST_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      }
    );
  },
};

/**
 * V19b is the independently selectable +100% companion to V19. It uses the
 * same bounded five-second request shape while keeping a distinct endpoint
 * and response field so the browser can render both targets simultaneously.
 */
export const pump100Next60mAPI = {
  getSeries: async (
    tokenAddress: string,
    params: Pump100Next60mSeriesParams,
    signal?: AbortSignal
  ): Promise<Pump100Next60mSeriesResponse> => {
    return api.get<Pump100Next60mSeriesResponse>(
      `/api/sol/tokens/${tokenAddress}/pump100_next60m_samples`,
      {
        params,
        timeout: PUMP_NEXT60M_REQUEST_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      }
    );
  },
};

/**
 * V20 estimates a +50% hit followed immediately by +45% in the next supported
 * 15-second VWAP bin, with at least $25 observed volume in each bin. It keeps
 * the same bounded five-second request grid while exposing a separately
 * versioned target and probability field.
 */
export const pump50HoldNext60mAPI = {
  getSeries: async (
    tokenAddress: string,
    params: Pump50HoldNext60mSeriesParams,
    signal?: AbortSignal
  ): Promise<Pump50HoldNext60mSeriesResponse> => {
    return api.get<Pump50HoldNext60mSeriesResponse>(
      `/api/sol/tokens/${tokenAddress}/pump50_hold_next60m_samples`,
      {
        params,
        timeout: PUMP_NEXT60M_REQUEST_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      }
    );
  },
};

/**
 * V21 estimates an entry-anchored +50% hit that holds +45% in the next
 * 15-second bin before any -30% stop relative to a realistic post-decision
 * entry price, within the next 60 minutes. It keeps the same bounded
 * five-second request grid while exposing a separately versioned target and
 * probability field. Its retained holdout found no evidence of edge.
 */
export const pump50EntryNext60mAPI = {
  getSeries: async (
    tokenAddress: string,
    params: Pump50EntryNext60mSeriesParams,
    signal?: AbortSignal
  ): Promise<Pump50EntryNext60mSeriesResponse> => {
    return api.get<Pump50EntryNext60mSeriesResponse>(
      `/api/sol/tokens/${tokenAddress}/pump50_entry_next60m_samples`,
      {
        params,
        timeout: PUMP_NEXT60M_REQUEST_TIMEOUT_MS,
        ...(signal ? { signal } : {}),
      }
    );
  },
};

/**
 * Token Score API types
 */
export interface ScoreListContribution {
  list_id: number | null;
  buyer_count: number;
  score_contribution: number;
  seller_count?: number;
  buy_volume_usd?: number;
  sell_volume_usd?: number;
  buy_pressure?: number;
  sell_pressure?: number;
  signal?: number;
}

/** Attribution for a single score sample. All scalar fields may be
 *  absent on legacy (pre-breakdown) analyses loaded from the DB. */
export interface ScoreBreakdown {
  base_score?: number;
  ratio_penalty?: number;
  mean_quality?: number;
  market_flow_multiplier?: number;
  market_flow_signal?: number;
  list_modifier?: number;
  list_signal?: number;
  momentum_multiplier?: number;
  buyer_count?: number;
  seller_count?: number;
  profiled_buyer_count?: number;
  /** Buyers that matched the alpha-list config (subset of buyer_count).
   *  V1 folds list membership into base_score; V2 exposes it separately
   *  through list_modifier/list_signal. */
  listed_buyer_count?: number;
  listed_seller_count?: number;
  weighted_seller_count?: number;
  buy_volume_usd?: number;
  sell_volume_usd?: number;
  entry_quality?: number;
  hold_quality?: number;
  roi_quality?: number;
  sizing_quality?: number;
  selectivity_quality?: number;
  reliability?: number;
  avg_median_entry_delta?: number;
  avg_minimum_entry_delta?: number;
  avg_median_first_sell_delta?: number;
  avg_minimum_first_sell_delta?: number;
  avg_tainted_percentage?: number;
  avg_total_realized_pnl_percentage?: number;
  avg_unsold_token_percentage?: number;
  avg_tokens_traded_count?: number;
  list_contributions?: ScoreListContribution[];
}

export interface ScoreSample {
  timestamp: number;
  score: number;
  breakdown?: ScoreBreakdown;
  /** Cumulative ∫ max(score - area_threshold, 0) dt up to this sample,
   *  in score-points × minutes. When area_window_seconds is set, this is
   *  the trailing-window area ending at the sample. */
  area_above_threshold?: number;
  /** Latching: true on the first sample where `area_above_threshold`
   *  crossed `area_cutoff` and on every sample after. */
  confirmed?: boolean;
}

export interface ScoreSamplesResponse {
  samples: ScoreSample[];
  metadata: {
    chain: string;
    token_address: string;
    algorithm: string;
    sample_count: number;
    range_start: number;
    range_end: number;
    time_window_minutes: number;
    min_interval_seconds?: number;
    effective_interval_seconds?: number;
    /** Excess threshold for the running area integral (default 5.0). */
    area_threshold?: number;
    /** Area-points × minutes required to flip `confirmed` true (default 200.0). */
    area_cutoff?: number;
    /** Optional trailing window for area calculation. When omitted, area is
     *  cumulative across the returned range. */
    area_window_seconds?: number | null;
    /** Unix timestamp at which `confirmed` first became true within the
     *  returned range, or null if the gate never crossed. */
    confirmed_at?: number | null;
    /** Final cumulative area at the last sample. */
    final_area?: number;
  };
}

export interface ScoreSamplesTooLargeError {
  error: string;
  estimated_sample_count: number;
  sync_ceiling: number;
}

export interface ScoreAnalysisResponse {
  id: number;
  algorithm: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number;
  samples: ScoreSample[];
  sample_count?: number;
  error_message?: string;
  metadata: Record<string, unknown>;
  created_at: number;
}

export interface ScoreSamplesParams {
  algorithm?: string;
  alpha_list_config?: string;
  time_window_minutes?: number;
  range_start: number;
  range_end: number;
  min_interval_seconds?: number;
  /** Excess threshold for the per-sample running area integral. Defaults
   *  server-side to 5.0 (Cohort B canonical). */
  area_threshold?: number;
  /** Area-points × minutes that flips the latching `confirmed` boolean.
   *  Defaults server-side to 200.0 (Cohort B canonical). */
  area_cutoff?: number;
  /** Optional trailing window for the area integral. */
  area_window_seconds?: number;
}

/**
 * Token Score API endpoints
 */
export interface ScoreAnalysisSummary {
  id: number;
  algorithm: string;
  status: string;
  sample_count: number;
  params: Record<string, unknown>;
  created_at: number;
}

export const tokenScoreAPI = {
  /** Synchronous score samples (max 100, fast) */
  getScoreSamples: async (
    chain: ChainId,
    tokenAddress: string,
    params?: ScoreSamplesParams
  ): Promise<ScoreSamplesResponse> => {
    return api.get<ScoreSamplesResponse>(`/api/${chain}/tokens/${tokenAddress}/score_samples`, {
      params,
    });
  },

  /** List past completed analyses for a token */
  listAnalyses: async (
    chain: ChainId,
    tokenAddress: string
  ): Promise<{ analyses: ScoreAnalysisSummary[] }> => {
    return api.get<{ analyses: ScoreAnalysisSummary[] }>(
      `/api/${chain}/tokens/${tokenAddress}/score_analyses`
    );
  },

  /** Start async high-resolution analysis */
  createScoreAnalysis: async (
    chain: ChainId,
    tokenAddress: string,
    params?: ScoreSamplesParams
  ): Promise<{ id: number; status: string }> => {
    return api.post<{ id: number; status: string }>(
      `/api/${chain}/tokens/${tokenAddress}/score_analysis`,
      params
    );
  },

  /** Poll async analysis result */
  getScoreAnalysis: async (
    chain: ChainId,
    tokenAddress: string,
    analysisId: number
  ): Promise<ScoreAnalysisResponse> => {
    return api.get<ScoreAnalysisResponse>(
      `/api/${chain}/tokens/${tokenAddress}/score_analysis/${analysisId}`
    );
  },
};

/**
 * @deprecated Use chainTokenAPI instead
 */
export const tokenAPI = {
  getToken: async (tokenAddress: string): Promise<TokenResponse> => {
    return chainTokenAPI.getToken('bsc', tokenAddress);
  },
  getLatestSwaps: async (
    tokenAddress: string,
    pairAddress: string,
    params?: { page?: number; per_page?: number }
  ): Promise<SwapsResponse> => {
    return chainTokenAPI.getLatestSwaps('bsc', tokenAddress, pairAddress, params);
  },
  getEarliestSwaps: async (
    tokenAddress: string,
    pairAddress: string,
    params?: { page?: number; per_page?: number }
  ): Promise<SwapsResponse> => {
    return chainTokenAPI.getEarliestSwaps('bsc', tokenAddress, pairAddress, params);
  },
  getTradesByWallet: async (
    tokenAddress: string,
    walletAddress: string,
    params?: { page?: number; per_page?: number }
  ): Promise<any> => {
    return chainTokenAPI.getTradesByWallet('bsc', tokenAddress, walletAddress, params);
  },
  getTradesByTraders: async (
    tokenAddress: string,
    traderAddresses: string[]
  ): Promise<TradesByTradersResponse> => {
    return chainTokenAPI.getTradesByTraders('bsc', tokenAddress, traderAddresses);
  },
  getTraders: async (
    tokenAddress: string,
    params?: {
      page?: number;
      per_page?: number;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
      earliest_buy_from?: string;
      earliest_buy_to?: string;
      buy_count_min?: string;
      buy_count_max?: string;
      sell_count_min?: string;
      sell_count_max?: string;
      bought_usd_min?: string;
      bought_usd_max?: string;
      sold_usd_min?: string;
      sold_usd_max?: string;
      realized_pnl_min?: string;
      realized_pnl_max?: string;
      pnl_percent_min?: string;
      pnl_percent_max?: string;
      tainted_pnl?: string;
    }
  ): Promise<TokenTradersResponse> => {
    return chainTokenAPI.getTraders('bsc', tokenAddress, params);
  },
};
